import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import Alert from '../components/common/Alert';
import SectorFormModal from '../components/products/SectorFormModal';
import SectorCard from '../components/products/SectorCard';
import ViewToggle from '../components/products/ViewToggle';
import { useStorePageGuard } from '../hooks/useStorePageGuard';
import { useAuth } from '../contexts/AuthContext';
import { Sector, CreateSectorPayload, UpdateSectorPayload, ViewMode } from '../types/sector';
import { getSectorsByStore, createSector, updateSector, deleteSector } from '../services/sectorService';
import { ApiErrorResponse } from '../services/authService';
import styles from './SectorsPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import placeholderImage from '../assets/placeholder.png';

const SectorsPage: React.FC = () => {
  const { loading, store, error } = useStorePageGuard();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';
  const isUser = currentUser?.role?.role_name?.toLowerCase() === 'user';
  const canAddSector = isAdmin || isUser;
  const canManageSector = isAdmin || isUser;

  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSector, setEditingSector] = useState<Sector | null>(null);

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    if (loading || error || !store) return;
    const fetchSectors = async () => {
      try {
        setIsLoading(true);
        hideAlert();
        const fetchedSectors = await getSectorsByStore(store.id);
        setSectors(fetchedSectors);
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to load sectors.', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSectors();
  }, [store, loading, error]);

  const handleAddSector = () => {
    setEditingSector(null);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleEditSector = (sector: Sector) => {
    setEditingSector(sector);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleDeleteSector = async (sectorId: number) => {
    if (window.confirm('Are you sure you want to delete this sector? This might affect products using it.')) {
      hideAlert();
      try {
        const response = await deleteSector(sectorId);
        setSectors(sectors.filter(s => s.id !== sectorId));
        showAlert(response.message || 'Sector deleted successfully!', 'success');
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to delete sector.', 'error');
      }
    }
  };

  const handleViewProducts = (sectorId: number) => {
    if (!store) return;
    window.location.href = `/store/${store.id}/products?sectorId=${sectorId}`;
  };

  const handleFormSubmit = async (formData: CreateSectorPayload | UpdateSectorPayload) => {
    hideAlert();
    setIsSubmitting(true);
    try {
      if (editingSector) {
        const response = await updateSector(editingSector.id, formData as UpdateSectorPayload);
        setSectors(sectors.map(s => s.id === editingSector.id ? response.sector : s));
        showAlert(response.message || 'Sector updated successfully!', 'success');
      } else {
        if (!store) {
          showAlert('Store not found. Please select a store.', 'error');
          setIsSubmitting(false);
          return;
        }
        const payloadWithStore = {
          ...formData,
          store_id: store.id
        } as CreateSectorPayload;
        const response = await createSector(payloadWithStore);
        setSectors([response.sector, ...sectors]);
        showAlert(response.message || 'Sector created successfully!', 'success');
      }
      setIsModalOpen(false);
      setEditingSector(null);
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || (editingSector ? 'Failed to update sector.' : 'Failed to create sector.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTableView = () => (
    <table className={tableStyles.dataTable}>
      <thead>
        <tr>
          <th>Image</th>
          <th>Name</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {sectors.map((sector) => (
          <tr key={sector.id}>
            <td className={tableStyles.imageCell}>
              {sector.image_url ? (
                <img 
                  src={sector.image_url} 
                  alt={sector.sector_name}
                  className={tableStyles.tableImage}
                  onError={(e) => {
                    e.currentTarget.src = placeholderImage;
                  }}
                />
              ) : (
                <div className={tableStyles.tablePlaceholder}>🏭</div>
              )}
            </td>
            <td>{sector.sector_name}</td>
            <td>
              <button onClick={() => handleViewProducts(sector.id)} className={`${tableStyles.actionButton} ${tableStyles.viewButton}`}>View Products</button>
              {canManageSector && (
                <>
                  <button onClick={() => handleEditSector(sector)} className={`${tableStyles.actionButton} ${tableStyles.editButton}`}>Edit</button>
                  <button onClick={() => handleDeleteSector(sector.id)} className={`${tableStyles.actionButton} ${tableStyles.deleteButton}`}>Delete</button>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderCardView = () => (
    <div className={cardGridStyles.cardsGrid}>
      {sectors.map((sector) => (
        <SectorCard
          key={sector.id}
          sector={sector}
          onEdit={canManageSector ? handleEditSector : () => {}}
          onDelete={canManageSector ? handleDeleteSector : () => {}}
          onViewProducts={handleViewProducts}
          canEdit={canManageSector}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading store information...</p>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.titleBar}>
              <h2 className={styles.title}>Sectors</h2>
            </div>
            <p className={styles.errorMessage}>{error}</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && sectors.length === 0) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading sectors...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert 
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />
          
          <div className={styles.titleBar}>
            <h2 className={styles.title}>Store Sectors</h2>
            <div className={styles.controls}>
              <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
              {canAddSector && (
                <button onClick={handleAddSector} className={styles.addButton}>Add Sector</button>
              )}
            </div>
          </div>

          {sectors.length === 0 && !isLoading && !alert.isVisible && (
            <p>No sectors found for this store. Click "Add Sector" to create one.</p>
          )}

          {sectors.length > 0 && (
            viewMode === 'table' ? renderTableView() : renderCardView()
          )}
          
          <SectorFormModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setEditingSector(null); hideAlert(); }}
            onSubmit={handleFormSubmit}
            initialData={editingSector}
            isLoading={isSubmitting}
          />
        </main>
      </div>
    </div>
  );
};

export default SectorsPage; 